
import { useState, useEffect, useRef } from 'react';

const Booking = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    inquiry: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [isVisible]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('inquiry', formData.inquiry);

      const response = await fetch('https://readdy.ai/api/form/d46dptl34biqimnfo2k0', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          inquiry: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      ref={sectionRef}
      id="contact" 
      className={`py-32 bg-white transition-all duration-[1500ms] ease-out ${
        isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div className="w-16 h-0.5 bg-luxury-navy mx-auto mb-6"></div>
            <h2 className="text-4xl font-light text-luxury-navy mb-4 tracking-tight">お問い合わせ</h2>
            <p className="text-base text-gray-600 font-light">
              ご質問やご要望がございましたら、お気軽にお問い合わせください
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* お問い合わせフォーム */}
            <div className="bg-white rounded-md p-10 border border-gray-200">
              <h3 className="text-xl font-medium text-luxury-navy mb-8 tracking-tight">メールでお問い合わせ</h3>
              <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                <div className="mb-6">
                  <label htmlFor="name" className="block text-sm font-medium text-luxury-navy mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-1 focus:ring-luxury-navy focus:border-luxury-navy text-sm font-light"
                    placeholder="山田太郎"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="email" className="block text-sm font-medium text-luxury-navy mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-1 focus:ring-luxury-navy focus:border-luxury-navy text-sm font-light"
                    placeholder="example@email.com"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="inquiry" className="block text-sm font-medium text-luxury-navy mb-2">
                    ご質問・ご要望 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="inquiry"
                    name="inquiry"
                    value={formData.inquiry}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-1 focus:ring-luxury-navy focus:border-luxury-navy text-sm resize-none font-light"
                    placeholder="ご質問やご要望をお聞かせください（500文字以内）"
                  />
                  <div className="text-right text-xs text-gray-500 mt-1 font-light">
                    {formData.inquiry.length}/500文字
                  </div>
                </div>

                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-luxury-blue/10 border border-luxury-blue/20 rounded-md">
                    <p className="text-luxury-blue text-sm font-light">
                      お問い合わせを送信いたしました。ありがとうございます。
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-800 text-sm font-light">
                      送信に失敗しました。もう一度お試しください。
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || formData.inquiry.length > 500}
                  className="w-full bg-luxury-navy text-white py-4 px-6 rounded-md font-medium text-sm hover:bg-opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer shadow-sm"
                >
                  {isSubmitting ? '送信中...' : 'お問い合わせを送信'}
                </button>
              </form>
            </div>

            {/* その他のお問い合わせ方法 */}
            <div className="space-y-6">
              {/* 公式LINE */}
              <div className="bg-white rounded-md p-10 border border-gray-200">
                <div className="text-center">
                  <div className="w-14 h-14 bg-luxury-navy/5 rounded-full flex items-center justify-center mx-auto mb-5">
                    <i className="ri-message-line text-luxury-navy text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-medium text-luxury-navy mb-3 tracking-tight">公式LINE</h3>
                  <p className="text-sm text-gray-600 mb-8 font-light">
                    LINEで気軽にお問い合わせ・ご相談いただけます
                  </p>
                  <button 
                    onClick={() => window.REACT_APP_NAVIGATE('/line')}
                    className="w-full bg-luxury-navy text-white py-3.5 px-6 rounded-md text-sm font-medium hover:bg-opacity-90 transition-all whitespace-nowrap cursor-pointer shadow-sm"
                  >
                    公式LINEでお問い合わせ
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Booking;
